import { AvatarUpload } from "@/components/AvatarUpload";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { type StatusBarItem, useWorkspacePage } from "@/components/workspace";
import { WorkspaceService } from "@/gen/omnios/v1/workspace_connect.js";
import { transport } from "@/lib/grpc";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { useWorkspaceStore } from "@/lib/workspace";
import { createClient } from "@connectrpc/connect";
import { createFileRoute } from "@tanstack/react-router";
import { Check, Loader2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/settings/general")({
	component: GeneralSettingsPage,
});

const client = createClient(WorkspaceService, transport);

function GeneralSettingsPage() {
	const { t } = useTranslation("settings");

	useBreadcrumbs([
		{
			label: t("breadcrumb_settings"),
			href: "/settings",
		},
		{ label: t("general_breadcrumb") },
	]);

	const { workspaces, selectedId, fetchWorkspaces } = useWorkspaceStore();

	const current = workspaces.find((w) => w.id === selectedId);

	const [name, setName] = useState(current?.name ?? "");
	const [slug, setSlug] = useState(current?.slug ?? "");
	const [dirty, setDirty] = useState(false);
	const [saving, setSaving] = useState(false);
	const [saved, setSaved] = useState(false);
	const [avatarUrl, setAvatarUrl] = useState(current?.avatarUrl ?? "");

	// Sync from store when workspace changes
	useEffect(() => {
		if (current) {
			setName(current.name);
			setSlug(current.slug);
			setDirty(false);
		}
	}, [current?.id]);

	// Autosave debounce
	const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		if (!dirty || !selectedId) return;
		if (timer.current) clearTimeout(timer.current);
		timer.current = setTimeout(async () => {
			setSaving(true);
			setSaved(false);
			try {
				await client.updateWorkspace({
					id: selectedId,
					name,
					slug,
				});
				await fetchWorkspaces();
				setDirty(false);
				setSaved(true);
				setTimeout(() => setSaved(false), 2000);
			} catch {
				// silently retry on next change
			} finally {
				setSaving(false);
			}
		}, 800);
		return () => {
			if (timer.current) clearTimeout(timer.current);
		};
	}, [dirty, name, slug, selectedId, fetchWorkspaces]);

	const statusBarItems = useMemo<StatusBarItem[]>(
		() => [
			{
				id: "status",
				content: saving
					? t("general_saving", { defaultValue: "Сохранение..." })
					: saved
						? `✓ ${t("general_saved")}`
						: dirty
							? t("general_unsaved", { defaultValue: "Не сохранено" })
							: "",
				align: "left" as const,
			},
		],
		[saving, saved, dirty, t],
	);
	useWorkspacePage({ statusBar: statusBarItems });

	const handleAvatarUpload = useCallback(
		async (file: File) => {
			const buf = await file.arrayBuffer();
			const resp = await client.uploadWorkspaceAvatar({
				data: new Uint8Array(buf),
				filename: file.name,
				mimeType: file.type,
			});
			setAvatarUrl(resp.avatarUrl);
			await fetchWorkspaces();
		},
		[fetchWorkspaces],
	);

	if (!current) {
		return (
			<div className="space-y-4">
				<p className="text-sm text-muted-foreground">
					{t("general_select_workspace")}
				</p>
			</div>
		);
	}

	const initials =
		current.name
			.split(/\s+/)
			.map((w) => w[0])
			.join("")
			.slice(0, 2)
			.toUpperCase() || "?";

	return (
		<div className="max-w-lg space-y-6 p-6 h-full overflow-auto">
			<div className="flex items-center gap-4">
				<AvatarUpload
					currentUrl={avatarUrl || current.avatarUrl}
					initials={initials}
					size={64}
					onUpload={handleAvatarUpload}
				/>
				<div className="space-y-1">
					<p className="text-base font-medium">{current.name}</p>
					<p className="text-sm text-muted-foreground">{current.slug}</p>
				</div>
			</div>

			<Separator />

			<div className="space-y-4">
				<div className="space-y-1.5">
					<Label>{t("general_label_name")}</Label>
					<Input
						value={name}
						onChange={(e) => {
							setName(e.target.value);
							setDirty(true);
						}}
					/>
				</div>
				<div className="space-y-1.5">
					<Label>Slug</Label>
					<Input
						value={slug}
						onChange={(e) => {
							setSlug(e.target.value);
							setDirty(true);
						}}
					/>
				</div>
				<div className="h-5">
					{saving && (
						<span className="flex items-center gap-1.5 text-xs text-muted-foreground">
							<Loader2 className="h-3.5 w-3.5 animate-spin" />
							{t("general_saving")}
						</span>
					)}
					{saved && (
						<span className="flex items-center gap-1.5 text-xs text-green-600">
							<Check className="h-3.5 w-3.5" />
							{t("general_saved")}
						</span>
					)}
				</div>
			</div>

			<Separator />

			<div className="space-y-2">
				<h3 className="text-sm font-medium">{t("general_info")}</h3>
				<div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
					<span className="text-muted-foreground">ID</span>
					<code className="text-xs">{current.id}</code>
					<span className="text-muted-foreground">Slug</span>
					<span>{current.slug}</span>
				</div>
			</div>
		</div>
	);
}
